import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Package, CheckCircle, Eye, Heart, Euro, Tag, Edit, Pause, Play, Trash2, Rocket, Star as StarIcon, X, Umbrella, CreditCard } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../context/AuthContext";
import api, { fileUrl } from "../lib/api";
import { formatPrice } from "../lib/format";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { BoostModal, FavoriteOfferModal } from "../components/modals";
import { toast } from "sonner";

function PlanMeter({ user, lang, onUpgrade }) {
  const limit = user?.plan_listing_limit ?? null;
  const used = user?.plan_listing_used ?? user?.listings_published_total ?? 0;
  const planName = { free: "Free", start: "Start", pro: "Pro", unlimited: "Unlimited" }[user?.plan || "free"];
  const nextPlan = { free: "Start", start: "Pro", pro: "Unlimited" }[user?.plan || "free"];
  const L = {
    es: { unlimited: "Anuncios ilimitados en tu plan Unlimited", of: (x, y, p) => `${x} anuncios de ${y} disponibles en tu plan ${p}`, upgrade: "Mejora tu plan", reached: "Has alcanzado el límite de tu plan" },
    en: { unlimited: "Unlimited listings on your Unlimited plan", of: (x, y, p) => `${x} of ${y} listings available on your ${p} plan`, upgrade: "Upgrade your plan", reached: "You've reached your plan limit" },
    it: { unlimited: "Annunci illimitati nel tuo piano Unlimited", of: (x, y, p) => `${x} annunci su ${y} disponibili nel tuo piano ${p}`, upgrade: "Fai upgrade del piano", reached: "Hai raggiunto il limite del tuo piano" },
  }[lang] || null;
  const tx = L || { unlimited: "Unlimited listings", of: (x, y, p) => `${x}/${y} · ${p}`, upgrade: "Upgrade", reached: "Limit reached" };

  if (limit === null) {
    return (
      <div data-testid="plan-meter" className="mb-6 rounded-2xl border border-purple-200 bg-purple-50/60 p-4 flex items-center gap-3">
        <span className="h-9 w-9 grid place-items-center rounded-xl bg-purple-100 text-purple-700"><StarIcon className="h-4.5 w-4.5" /></span>
        <p className="text-sm font-medium text-purple-800">{tx.unlimited}</p>
      </div>
    );
  }
  const pct = Math.min(100, Math.round((used / limit) * 100));
  const atLimit = used >= limit;
  const barColor = atLimit ? "bg-rose-500" : pct >= 70 ? "bg-amber-500" : "bg-emerald-500";
  const textColor = atLimit ? "text-rose-700" : pct >= 70 ? "text-amber-700" : "text-slate-600";
  return (
    <div data-testid="plan-meter" className={`mb-6 rounded-2xl border p-4 ${atLimit ? "border-rose-200 bg-rose-50/60" : "border-slate-200 bg-white"}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><Package className="h-4 w-4 text-[#008080]" />{planName}</span>
        <span data-testid="plan-meter-count" className={`text-sm font-mono-badge font-bold ${textColor}`}>{used} / {limit}</span>
      </div>
      <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden">
        <div data-testid="plan-meter-bar" className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${Math.max(pct, used > 0 ? 6 : 0)}%` }} />
      </div>
      <p className={`text-xs mt-2 ${textColor}`}>{atLimit ? tx.reached + " · " : ""}{tx.of(used, limit, planName)}</p>
      {atLimit && (
        <Button data-testid="plan-meter-upgrade" onClick={onUpgrade} className="mt-3 h-10 w-full bg-rose-500 hover:bg-rose-600 text-white font-semibold">
          <Rocket className="h-4 w-4 mr-1.5" />{tx.upgrade}{nextPlan ? ` → ${nextPlan}` : ""}
        </Button>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, color, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className="text-left bg-white border border-slate-200 rounded-2xl p-4 hover:border-teal-300 hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-teal-500/40">
      <span className={`h-9 w-9 grid place-items-center rounded-xl ${color} mb-2`}><Icon className="h-4.5 w-4.5" /></span>
      <p className="font-display font-extrabold text-2xl">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </button>
  );
}

export default function Dashboard() {
  const { t, lang } = useI18n();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [offers, setOffers] = useState([]);
  const [vacBusy, setVacBusy] = useState(false);
  const [tab, setTab] = useState("listings");
  const [filter, setFilter] = useState(null); // {status?, sort?, key}
  const [boostListing, setBoostListing] = useState(null);
  const [favOfferListing, setFavOfferListing] = useState(null);

  const goListings = (f) => { setFilter(f); setTab("listings"); };
  const load = useCallback(async () => {
    try {
      const [d, o] = await Promise.all([api.get("/dashboard"), api.get("/offers?role=received")]);
      setData(d.data); setOffers(o.data);
    } catch {}
  }, []);
  useEffect(() => { if (user === false) navigate("/auth"); else if (user) load(); }, [user, navigate, load]);

  const displayed = useMemo(() => {
    let arr = [...(data?.listings || [])];
    if (filter?.status) arr = arr.filter((l) => l.status === filter.status);
    if (filter?.sort === "views") arr.sort((a, b) => (b.views || 0) - (a.views || 0));
    if (filter?.sort === "favs") arr.sort((a, b) => (b.fav_count || 0) - (a.fav_count || 0));
    return arr;
  }, [data, filter]);

  if (!data) return <div className="p-10 text-center text-slate-400">{t("common.loading")}</div>;

  const updateStatus = async (id, status) => { await api.put(`/listings/${id}`, { status }); toast.success(t("common.saved")); load(); };
  const del = async (id) => { await api.delete(`/listings/${id}`); toast.success(t("dashboard.delete")); load(); };
  const offerAct = async (id, action) => {
    let counter_amount = null;
    if (action === "counter") { const v = prompt(t("offer.counterAmount")); if (!v) return; counter_amount = Number(v); }
    await api.post(`/offers/${id}/action`, { action, counter_amount }); toast.success("OK"); load();
  };

  const filterLabel = filter?.status ? t(`dashboard.statuses.${filter.status}`)
    : filter?.sort === "views" ? t("dashboard.views")
    : filter?.sort === "favs" ? t("dashboard.favorites") : null;

  const statusColors = { activo: "bg-emerald-100 text-emerald-700", vendido: "bg-slate-200 text-slate-600", pausado: "bg-amber-100 text-amber-700", reservado: "bg-blue-100 text-blue-700" };

  const toggleVacation = async () => {
    setVacBusy(true);
    try {
      const { data: u } = await api.post("/seller/vacation", { on: !user?.on_vacation });
      setUser(u);
      toast.success(u.on_vacation ? t("dashboard.vacationOn") : t("dashboard.vacationOff"));
    } catch { toast.error("Error"); } finally { setVacBusy(false); }
  };

  return (
    <div className="px-4 sm:px-8 py-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <h1 className="font-display font-extrabold text-2xl sm:text-3xl">{t("dashboard.title")}</h1>
        <Button data-testid="vacation-toggle-btn" onClick={toggleVacation} disabled={vacBusy}
          className={user?.on_vacation ? "bg-emerald-500 hover:bg-emerald-600 h-11" : "bg-amber-500 hover:bg-amber-600 h-11"}>
          <Umbrella className="h-4 w-4 mr-1.5" />{user?.on_vacation ? t("dashboard.reopenShop") : t("dashboard.vacationShop")}
        </Button>
      </div>
      {user?.on_vacation && (
        <div data-testid="vacation-banner" className="mb-6 p-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-800 text-sm flex items-center gap-2">
          <Umbrella className="h-5 w-5 shrink-0" />{t("dashboard.vacationBanner")}
        </div>
      )}

      <PlanMeter user={user} lang={lang} onUpgrade={() => navigate("/planes")} />


      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        <Stat testid="kpi-active" icon={Package} label={t("dashboard.active")} value={data.active} color="bg-teal-100 text-teal-700" onClick={() => goListings({ status: "activo" })} />
        <Stat testid="kpi-sold" icon={CheckCircle} label={t("dashboard.sold")} value={data.sold} color="bg-emerald-100 text-emerald-700" onClick={() => goListings({ status: "vendido" })} />
        <Stat testid="kpi-views" icon={Eye} label={t("dashboard.views")} value={data.views} color="bg-blue-100 text-blue-700" onClick={() => goListings({ sort: "views" })} />
        <Stat testid="kpi-favorites" icon={Heart} label={t("dashboard.favorites")} value={data.favorites} color="bg-rose-100 text-rose-700" onClick={() => goListings({ sort: "favs" })} />
        <Stat testid="kpi-offers" icon={Tag} label={t("dashboard.offers")} value={data.offers} color="bg-amber-100 text-amber-700" onClick={() => setTab("offers")} />
        <Stat testid="kpi-revenue" icon={Euro} label={t("dashboard.revenue")} value={formatPrice(data.revenue, lang)} color="bg-purple-100 text-purple-700" onClick={() => goListings({ status: "vendido" })} />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="listings" data-testid="tab-listings">{t("dashboard.myListings")}</TabsTrigger>
          <TabsTrigger value="offers" data-testid="tab-offers">{t("offer.received")} ({offers.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="listings">
          {filterLabel && (
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs text-slate-500">{t("search.filters")}:</span>
              <span data-testid="active-filter-chip" className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-teal-50 text-teal-700 border border-teal-200">
                {filterLabel}
                <button data-testid="clear-filter-btn" onClick={() => setFilter(null)}><X className="h-3 w-3" /></button>
              </span>
            </div>
          )}
          {(filter ? displayed : data.listings).length === 0 ? (
            <div className="text-center py-16 text-slate-400">
              <p>{t("dashboard.noListings")}</p>
              <Link to="/sell"><Button className="mt-4 bg-[#008080] hover:bg-[#006666]">{t("common.sellNow")}</Button></Link>
            </div>
          ) : (
            <div className="space-y-3">
              {(filter ? displayed : data.listings).map((l) => (
                <div key={l.id} data-testid={`dash-listing-${l.id}`} className="flex items-center gap-4 bg-white border border-slate-200 rounded-2xl p-3">
                  <Link to={`/listing/${l.id}`} className="h-16 w-16 rounded-xl overflow-hidden bg-slate-100 shrink-0">
                    {l.photos?.[0] && <img src={fileUrl(l.photos[0])} alt="" className="w-full h-full object-cover" />}
                  </Link>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{l.title}</p>
                    {l.ref && <p data-testid={`dash-ref-${l.id}`} className="text-[10px] font-mono text-slate-400">{l.ref}</p>}
                    <p className="text-[#008080] font-bold text-sm">{formatPrice(l.price, lang)}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${statusColors[l.status]}`}>{t(`dashboard.statuses.${l.status}`)}</span>
                      <span className="text-[10px] text-slate-400"><Eye className="h-3 w-3 inline" /> {l.views}</span>
                      <span className="text-[10px] text-slate-400"><Heart className="h-3 w-3 inline" /> {l.fav_count || 0}</span>
                      {l.is_boosted && <span className="text-[10px] text-[#FF9F1C]">★</span>}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 justify-end">
                    <button data-testid={`edit-${l.id}`} onClick={() => navigate(`/sell?edit=${l.id}`)} title={t("dashboard.edit")} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-slate-100"><Edit className="h-4 w-4 text-slate-600" /></button>
                    {l.status === "activo" ? (
                      <button data-testid={`pause-${l.id}`} onClick={() => updateStatus(l.id, "pausado")} title={t("dashboard.pause")} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-slate-100"><Pause className="h-4 w-4 text-slate-600" /></button>
                    ) : (
                      <button data-testid={`resume-${l.id}`} onClick={() => updateStatus(l.id, "activo")} title={t("dashboard.resume")} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-slate-100"><Play className="h-4 w-4 text-slate-600" /></button>
                    )}
                    <button data-testid={`sold-${l.id}`} onClick={() => updateStatus(l.id, "vendido")} title={t("dashboard.markSold")} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-slate-100"><CheckCircle className="h-4 w-4 text-emerald-600" /></button>
                    <button data-testid={`boost-${l.id}`} onClick={() => setBoostListing(l)} title={t("dashboard.promote")} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-amber-50"><Rocket className="h-4 w-4 text-[#FF9F1C]" /></button>
                    <button data-testid={`fav-offer-${l.id}`} onClick={() => setFavOfferListing(l)} title={t("favOffer.button")} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-rose-50"><Tag className="h-4 w-4 text-rose-500" /></button>
                    <button data-testid={`delete-${l.id}`} onClick={() => del(l.id)} title={t("dashboard.delete")} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-rose-50"><Trash2 className="h-4 w-4 text-rose-500" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="offers">
          {offers.length === 0 ? <div className="text-center py-16 text-slate-400">{t("offer.none")}</div> : (
            <div className="space-y-3">
              {offers.map((o) => (
                <div key={o.id} className="bg-white border border-slate-200 rounded-2xl p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{o.listing_title}</p>
                      <p className="text-[#008080] font-bold text-lg">{formatPrice(o.amount, lang)}</p>
                      {o.message && <p className="text-xs text-slate-500 mt-1">"{o.message}"</p>}
                    </div>
                    <span className="text-xs px-2 py-1 rounded-full bg-slate-100">{t(`offer.${o.status === "pending" ? "pending" : o.status === "accepted" ? "accepted" : o.status === "countered" ? "countered" : "rejected"}`)}</span>
                  </div>
                  {o.status === "pending" && (
                    <div className="flex gap-2 mt-3">
                      <Button data-testid={`dash-accept-offer-${o.id}`} onClick={() => offerAct(o.id, "accept")} className="flex-1 h-9 bg-emerald-500 hover:bg-emerald-600">{t("offer.accept")}</Button>
                      <Button data-testid={`dash-reject-offer-${o.id}`} onClick={() => offerAct(o.id, "reject")} variant="outline" className="flex-1 h-9">{t("offer.reject")}</Button>
                      <Button data-testid={`dash-counter-offer-${o.id}`} onClick={() => offerAct(o.id, "counter")} variant="outline" className="flex-1 h-9">{t("offer.counter")}</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {boostListing && (
        <BoostModal open={!!boostListing} onOpenChange={(o) => !o && setBoostListing(null)} listing={boostListing} onDone={() => { setBoostListing(null); load(); }} />
      )}
      {favOfferListing && (
        <FavoriteOfferModal open={!!favOfferListing} onOpenChange={(o) => !o && setFavOfferListing(null)} listing={favOfferListing} onDone={() => setFavOfferListing(null)} />
      )}
    </div>
  );
}
